import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { Header } from '../components/shared/Header/Header';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@/components/icons/DownloadIcon';
import PrintIcon from '@/components/icons/PrintIcon';
import { AssessmentPDFDocument, AssessmentData } from '../components/AssessmentPDFDocument';
import { TextBox } from '../components/TextBox';
import { BarChart } from '../components/BarChart';

const ReportDasboardSummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  
  const [data] = useState<AssessmentData>({
    score: 42,
    definitions: [
      {
        title: "Strategic Sub-domain: Organizational Infrastructure Definition",
        text: "Organizational infrastructure represents a sustained systemic pattern that encompasses the foundational structural foundations most notably in its function effectively."
      },
      {
        title: "Strategic Sub-domain: Comprehensive Framework Definition",
        text: "This subdomain ensurs and their exact pattern needs to reference objective other than the categorization has its always and force, deals with are particularly the productive operations."
      },
      {
        title: "Reinforcing Sub-domain: Strategy Definition",
        text: "Business are the rules that shown how a successful operation-influence cross, discriminating/analysis process and integral other elements."
      },
      {
        title: "Reinforcing Sub-domain: Approximation Definition",
        text: "Approximation is the social sub-domain systems, intellectual under standards; creating a personalized activity that new elements, new insults, and contact based intensives from emotional liability."
      },
      {
        title: "Reinforcing Sub-domain: SQL Tax Exempt Status Definition",
        text: "SQL Tax Exempt Status is fundamental to organizational economic frameworks and their capabilities to the key-both milestones, at accurate, a separate application when incorporating and concordance with organization-life systems."
      },
      {
        title: "Reinforcing Sub-domain: Business/workflow Perspectives Definition",
        text: "Business workflow perspectives advocate a concordance process of each local level preparation such as concurrent commercial, central structures and techniques."
      },
      {
        title: "Reinforcing Sub-domain: Illustrative and Risk Management Definition",
        text: "Illustrative and Risk Management of the concurrent service-management critical contextual issues with relative on operational roles and support context assurance expertise."
      },
      {
        title: "Reinforcing Sub-domain: Illustrative and Risk Management Definition",
        text: "Illustrative and Risk Management of the concurrent service-management critical contextual issues with relative on operational roles and support context assurance expertise."
      },
    ],
    barChartData: [
      { label: "Planning", value: 85 },
      { label: "Research", value: 70 },
      { label: "Execution", value: 92 },
      { label: "Analysis", value: 78 },
      { label: "Reporting", value: 88 }
    ]
  });

  useEffect(() => {
    const loadData = (): void => {
      setLoading(true);
      
      // Simulate API delay
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    };

    loadData();
  }, [id]);

  const handlePrint = async (): Promise<void> => {
    try {
      const pdfDoc = <AssessmentPDFDocument data={data} />;
      const asPdf = pdf(pdfDoc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch (error) {
      console.error('Error generating PDF for print:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">{t('assessmentPDF.loadingData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
              <Header 
          backUrl="/reports"
        rightContent={
          <div className="flex flex-row gap-2">
            <PDFDownloadLink
              document={<AssessmentPDFDocument data={data} />}
              fileName="assessment-report.pdf"
              className="bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              {({ loading: pdfLoading }: { loading: boolean }) => (
                <>
                  <DownloadIcon className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm sm:text-base">
                    {pdfLoading ? t('assessmentPDF.generating') : t('assessmentPDF.downloadPDF')}
                  </span>
                </>
              )}
            </PDFDownloadLink>
            
            <button
              onClick={handlePrint}
              className="bg-[#5E9E2E] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#4c8025] transition-colors flex items-center justify-center gap-2"
              type="button"
            >
              <PrintIcon className="w-5 h-5" />
              <span className="hidden sm:inline text-sm sm:text-base">{t('assessmentPDF.printPDF')}</span>
            </button>
          </div>
        }
      />

      <div className="mt-[20px]">
        <div className="mx-auto bg-white rounded-lg shadow-sm overflow-hidden" style={{ aspectRatio: '4/3' }}>
          <div className="flex h-full">
            <div className="w-3/5 bg-assessment-green text-white p-6 relative">
              <div className="absolute top-0 left-0 right-0 bg-white text-gray-800 p-4">
                <div className="flex justify-between items-center">
                  <h1 className="text-sm font-semibold">{t('assessmentPDF.organizationalInfrastructure')}</h1>
                </div>
              </div>

              <div className="pt-9">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold">{t('assessmentPDF.subdomainDefinition')}</h2>
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center border-2 border-white">
                    <span className="text-xl font-bold">{data.score}%</span>
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  {data.definitions.map((definition, index) => (
                    <div key={index} className="pl-4">
                      <div className="font-semibold mb-1">
                        {index + 1}. {definition.title}
                      </div>
                      <div className="text-white text-opacity-90 text-justify">
                        {definition.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-2/5 p-6 space-y-6">
              <TextBox />
              <BarChart data={data.barChartData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDasboardSummaryPage;